import React, { useState } from 'react';
import { encryptString } from '../../services/apiUtils';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../../components/Navbar';
import '../../assets/scss/app.scss';

const StringEncryption = () => {
    const [inputText, setInputText] = useState('');
    const [encryptedText, setEncryptedText] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = (e) => {
        setInputText(e.target.value);
    };

    const handleEncrypt = async () => {
        if (!inputText.trim()) {
            toast.error('Please enter text to encrypt');
            return;
        }

        setIsLoading(true);
        try {
            const encrypted = await encryptString(inputText);
            setEncryptedText(encrypted);
            toast.success('Text encrypted successfully!');
        } catch (error) {
            console.error('Encryption failed:', error);
            toast.error('Failed to encrypt text. Please try again.');
            setEncryptedText('');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = () => {
        setInputText('');
        setEncryptedText('');
    };

    const handleCopyToClipboard = () => {
        if (encryptedText) {
            navigator.clipboard.writeText(encryptedText)
                .then(() => {
                    toast.success('Encrypted text copied to clipboard!');
                })
                .catch(() => {
                    toast.error('Failed to copy to clipboard');
                });
        }
    };

    return (
        <div className="mainFlow">
            <Navbar />
            <ToastContainer />
            
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6">
                        <div className="card shadow">
                            <div className="card-header bg-primary text-white text-center">
                                <h4 className="mb-0">String Encryption Utility</h4>
                            </div>
                            <div className="card-body p-4">
                                <div className="mb-3">
                                    <label htmlFor="inputText" className="form-label fw-bold">
                                        Enter text to encrypt:
                                    </label>
                                    <textarea
                                        id="inputText"
                                        className="form-control"
                                        rows="4"
                                        value={inputText}
                                        onChange={handleInputChange}
                                        placeholder="Type your text here..."
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className="d-grid gap-2 d-md-flex justify-content-md-center mb-3">
                                    <button
                                        className="btn btn-success"
                                        onClick={handleEncrypt}
                                        disabled={isLoading || !inputText.trim()}
                                    >
                                        {isLoading ? (
                                            <>
                                                <span 
                                                    className="spinner-border spinner-border-sm me-2" 
                                                    role="status" 
                                                    aria-hidden="true"
                                                ></span>
                                                Encrypting...
                                            </>
                                        ) : (
                                            'Encrypt Text'
                                        )}
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={handleClear}
                                        disabled={isLoading}
                                    >
                                        Clear All
                                    </button>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="encryptedText" className="form-label fw-bold">
                                        Encrypted Output:
                                    </label>
                                    <textarea
                                        id="encryptedText"
                                        className="form-control"
                                        rows="6"
                                        value={encryptedText}
                                        readOnly
                                        placeholder="Encrypted text will appear here..."
                                        style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                    />
                                </div>

                                {encryptedText && (
                                    <div className="d-grid">
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={handleCopyToClipboard}
                                        >
                                            <i className="fas fa-copy me-2"></i>
                                            Copy to Clipboard
                                        </button>
                                    </div>
                                )}

                                <div className="alert alert-info mt-3" role="alert">
                                    <small>
                                        <strong>Note:</strong> This utility uses RSA-OAEP encryption with SHA-1 hashing. 
                                        The encrypted output is base64 encoded for safe transmission.
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StringEncryption;